import React, { useState, useEffect } from 'react';

import { CenteredModal, Tooltip } from "~/components";
import databaseApi from '~/api/databaseApi';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faDatabase, faCopy, faExchangeAlt, faSync, faTrash, faInfoCircle, faArrowLeft, faArrowRight, faEye, faEyeSlash, faExclamationTriangle, faBoxOpen, faExclamationCircle, faCheckCircle, faHistory, faTable, faAngleLeft, faAngleRight, faAngleDoubleLeft, faAngleDoubleRight, faCode } from "@fortawesome/free-solid-svg-icons";
import cx from 'classnames';
import Select from 'react-select';
import * as signalR from '@microsoft/signalr';
import './DatabaseManagementModal.css';
import '~/pages/authentications/auth.css';
import HistoryTab from './HistoryTab';
import ViewScriptTab from './ViewScriptTab';
import ReactDiffViewer from 'react-diff-viewer-continued';

function DatabaseManagementModal({ show, onClose }) {
    const [activeTab, setActiveTab] = useState('hub'); // hub, list, clone, compare, viewData
    const [databases, setDatabases] = useState([]);
    const [loading, setLoading] = useState(false);

    // Clone state
    const [cloneSource, setCloneSource] = useState('');
    const [cloneTarget, setCloneTarget] = useState('');
    const [cloning, setCloning] = useState(false);
    const [cloneProgress, setCloneProgress] = useState(0);
    const [cloneTimeLeft, setCloneTimeLeft] = useState('');
    const [cloneMessage, setCloneMessage] = useState('');
    const [cloneCurrentItem, setCloneCurrentItem] = useState('');
    const [cloneLogs, setCloneLogs] = useState([]);
    const [showLogsPanel, setShowLogsPanel] = useState(false);

    // Compare state
    const [db1, setDb1] = useState('');
    const [db2, setDb2] = useState('');
    const [objectsDb1, setObjectsDb1] = useState({ Tables: [], Views: [], StoredProcedures: [], Functions: [] });
    const [objectsDb2, setObjectsDb2] = useState({ Tables: [], Views: [], StoredProcedures: [], Functions: [] });
    const [loadingDb1, setLoadingDb1] = useState(false);
    const [loadingDb2, setLoadingDb2] = useState(false);
    const [transferringItem, setTransferringItem] = useState(null);
    const [blinkingItem, setBlinkingItem] = useState(null);
    const [compareSubTab, setCompareSubTab] = useState('Tables');

    // Compare object state
    const [compareModalConfig, setCompareModalConfig] = useState(null);
    const [loadingCompareObject, setLoadingCompareObject] = useState(false);

    // Delete state
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [dbToDelete, setDbToDelete] = useState('');
    const [deletePassword, setDeletePassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [deleteMessage, setDeleteMessage] = useState('');
    const [transferringDetails, setTransferringDetails] = useState(null);
    const [transferMessage, setTransferMessage] = useState({ text: '', type: '' });

    // Details state
    const [selectedDbDetails, setSelectedDbDetails] = useState(null);
    const [tableSizes, setTableSizes] = useState([]);
    const [loadingDetails, setLoadingDetails] = useState(false);

    // View Data state
    const [viewDataSelectedDb, setViewDataSelectedDb] = useState(null);
    const [viewDataTables, setViewDataTables] = useState([]);
    const [viewDataSelectedTable, setViewDataSelectedTable] = useState(null);
    const [viewDataRows, setViewDataRows] = useState([]);
    const [viewDataColumns, setViewDataColumns] = useState([]);
    const [viewDataLoading, setViewDataLoading] = useState(false);
    const [viewDataError, setViewDataError] = useState('');
    
    // Pagination state
    const [pageIndex, setPageIndex] = useState(0);
    const [pageSize, setPageSize] = useState(500);
    const [totalRows, setTotalRows] = useState(0);

    useEffect(() => {
        if (show) {
            fetchDatabases();
        }
    }, [show]);

    useEffect(() => {
        if (db1) fetchObjects(db1, setObjectsDb1, setLoadingDb1);
        else setObjectsDb1({ Tables: [], Views: [], StoredProcedures: [], Functions: [] });
    }, [db1]);

    useEffect(() => {
        if (db2) fetchObjects(db2, setObjectsDb2, setLoadingDb2);
        else setObjectsDb2({ Tables: [], Views: [], StoredProcedures: [], Functions: [] });
    }, [db2]);

    useEffect(() => {
        if (viewDataSelectedDb) {
            fetchViewDataTables(viewDataSelectedDb);
        } else {
            setViewDataTables([]);
            setViewDataSelectedTable(null);
            setViewDataRows([]);
            setViewDataColumns([]);
            setTotalRows(0);
        }
    }, [viewDataSelectedDb]);

    useEffect(() => {
        if (viewDataSelectedTable && viewDataSelectedDb) {
            handleFetchViewData(pageIndex, pageSize);
        }
    }, [pageIndex, pageSize, viewDataSelectedTable]);

    const fetchViewDataTables = async (dbName) => {
        setViewDataLoading(true);
        try {
            const res = await databaseApi.getTablesByDatabase(dbName);
            if (res.data?.Succeeded || res.data?.succeeded) {
                const tables = Array.isArray(res.data.Data) ? res.data.Data : (Array.isArray(res.data.data) ? res.data.data : res.data);
                setViewDataTables(tables);
            }
        } catch (e) {
            setViewDataError('Failed to fetch tables');
        }
        setViewDataLoading(false);
    };

    const handleFetchViewData = async (pageIdx = 0, limit = 500) => {
        if (!viewDataSelectedDb || !viewDataSelectedTable) return;
        setViewDataLoading(true);
        setViewDataError('');
        try {
            const dbName = viewDataSelectedDb.replace(/\]/g, ']]');
            const schemaName = viewDataSelectedTable.SchemaName.replace(/\]/g, ']]');
            const tableName = viewDataSelectedTable.TableName.replace(/\]/g, ']]');
            
            // Fetch total rows if first page or changed table
            const countQuery = `SELECT COUNT(*) AS TotalRows FROM [${dbName}].[${schemaName}].[${tableName}]`;
            const countRes = await databaseApi.executeQuery(countQuery);
            let total = totalRows;
            if (countRes.data?.Succeeded || countRes.data?.succeeded) {
                const data = countRes.data.Data || countRes.data.data || countRes.data;
                const rows = data.Rows || data.rows;
                if (rows && rows.length > 0) {
                    total = rows[0].TotalRows || rows[0].totalrows || rows[0].totalRows || 0;
                    setTotalRows(total);
                }
            }

            // Calculate offset
            const offset = pageIdx * limit;
            
            // Get PK column or use a dummy sort if PK is not guaranteed. 
            // In SQL Server, OFFSET must be paired with ORDER BY.
            // Using ORDER BY (SELECT NULL) works universally.
            const query = `
                SELECT * FROM [${dbName}].[${schemaName}].[${tableName}]
                ORDER BY (SELECT NULL)
                OFFSET ${offset} ROWS
                FETCH NEXT ${limit} ROWS ONLY;
            `;
            const res = await databaseApi.executeQuery(query);
            if (res.data?.Succeeded || res.data?.succeeded) {
                const data = res.data.Data || res.data.data || res.data;
                setViewDataColumns(data.Columns || data.columns || []);
                setViewDataRows(data.Rows || data.rows || []);
            } else {
                setViewDataError(res.data?.ErrorMessage || res.data?.errorMessage || 'Failed to fetch data');
            }
        } catch (e) {
            setViewDataError(e.message || 'Error occurred');
        }
        setViewDataLoading(false);
    };

    const fetchDatabases = async () => {
        setLoading(true);
        try {
            const res = await databaseApi.getAllDatabases();
            if (res.data?.Succeeded || res.data?.succeeded || Array.isArray(res.data)) {
                setDatabases(Array.isArray(res.data) ? res.data : (res.data?.Data ?? res.data?.data ?? []));
            }
        } catch (error) {
            // Error silently handled
        }
        setLoading(false);
    };

    const handleViewDetails = async (dbName) => {
        setSelectedDbDetails(dbName);
        setLoadingDetails(true);
        try {
            const res = await databaseApi.getDatabaseTableSizes(dbName);
            if (res.data?.Succeeded || res.data?.succeeded || Array.isArray(res.data)) {
                setTableSizes(Array.isArray(res.data) ? res.data : (res.data?.Data ?? res.data?.data ?? []));
            }
        } catch (error) {
            // Error silently handled
        }
        setLoadingDetails(false);
    };

    const handleBackToList = () => {
        setSelectedDbDetails(null);
        setTableSizes([]);
    };

    const fetchObjects = async (dbName, setObjectsFn, setLoadingFn, silent = false) => {
        if (!silent) setLoadingFn(true);
        try {
            const res = await databaseApi.getDatabaseObjects(dbName);
            if (res.data?.Succeeded || res.data?.succeeded) {
                const data = res.data.Data || res.data.data || res.data;

                // Normalizing keys to PascalCase so it matches the UI subtabs
                const normalizedData = {
                    Tables: data.Tables || data.tables || [],
                    Views: data.Views || data.views || [],
                    StoredProcedures: data.StoredProcedures || data.storedProcedures || [],
                    Functions: data.Functions || data.functions || []
                };
                setObjectsFn(normalizedData);
            }
        } catch (error) {
            setObjectsFn({ Tables: [], Views: [], StoredProcedures: [], Functions: [] });
            setTransferMessage({ text: `Error fetching objects for ${dbName}: ${error.response?.data?.Message || error.message}`, type: 'error' });
        }
        if (!silent) setLoadingFn(false);
    };

    const handleClone = async (e) => {
        e.preventDefault();
        if (!cloneSource || !cloneTarget) {
            setCloneMessage('Please select a source and provide a target database name.');
            return;
        }

        setCloning(true);
        setShowLogsPanel(true);
        setCloneProgress(0);
        setCloneMessage('');
        setCloneTimeLeft('Connecting to server...');
        setCloneLogs(['[INFO] Initializing cloning process...', '[INFO] Creating empty target database (this may take 10-20 seconds)...']);

        const currentJobId = `clone_${Date.now()}`;
        const startTime = Date.now();
        const apiUrl = process.env.REACT_APP_API_URL;

        let timerInterval = null;
        let connection = null;

        try {
            // 1. Establish SignalR connection
            connection = new signalR.HubConnectionBuilder()
                .withUrl(`${apiUrl}/api/databaseHub`)
                .withAutomaticReconnect()
                .build();

            connection.on("ReceiveCloneProgress", (data) => {
                const pct = data.Percent;
                const currentItem = data.CurrentItem;

                if (pct === -1) {
                    setCloneMessage('Error: ' + currentItem);
                    if (currentItem) {
                        setCloneLogs(prev => [...prev, `[ERROR] ${currentItem}`]);
                    }
                    cleanup(false);
                } else {
                    setCloneProgress(pct);
                    if (currentItem) {
                        setCloneLogs(prev => [...prev, currentItem]);
                    }
                    if (pct === 100) {
                        setCloneMessage('Database schema cloned successfully!');
                        fetchDatabases();
                        setCloneTarget('');
                        cleanup(true);
                    }
                }
            });

            await connection.start();
            await connection.invoke("JoinJobGroup", currentJobId);

            // Start elapsed time timer
            timerInterval = setInterval(() => {
                const elapsedSeconds = Math.floor((Date.now() - startTime) / 1000);
                const mins = Math.floor(elapsedSeconds / 60);
                const secs = elapsedSeconds % 60;
                setCloneTimeLeft(`Time elapsed: ${mins}m ${secs}s`);
            }, 1000);

            // 2. Trigger backend task
            const res = await databaseApi.cloneSchema(cloneSource, cloneTarget, currentJobId);

            if (!(res.data?.Succeeded || res.data?.succeeded || res.data === true)) {
                const errMessage = res.data?.Message || res.data?.message || res.data?.ErrorMessage || res.data?.errorMessage || 'Failed to start cloning task.';
                setCloneMessage(errMessage);
                setCloneLogs(prev => [...prev, `[ERROR] ${errMessage}`]);
                cleanup(false);
            }
        } catch (error) {
            const errMessage = error.response?.data?.Message || error.message || 'Error connecting to real-time service.';
            setCloneMessage(errMessage);
            setCloneLogs(prev => [...prev, `[ERROR] ${errMessage}`]);
            cleanup(false);
        }

        function cleanup(success) {
            if (timerInterval) clearInterval(timerInterval);
            if (connection) {
                connection.invoke("LeaveJobGroup", currentJobId).catch(console.error).finally(() => connection.stop());
            }
            setCloning(false);
        }
    };

    const [transferModalConfig, setTransferModalConfig] = useState(null);
    const [transferModeSelection, setTransferModeSelection] = useState('Data');
    const [keepIdentity, setKeepIdentity] = useState(true);

    useEffect(() => {
        if (transferMessage?.text) {
            const timer = setTimeout(() => {
                setTransferMessage({ text: '', type: '' });
            }, 5000);
            return () => clearTimeout(timer);
        }
    }, [transferMessage]);

    const [deleteTableModalConfig, setDeleteTableModalConfig] = useState(null);
    const [deleteTableAction, setDeleteTableAction] = useState('truncate');
    const [tableDependencies, setTableDependencies] = useState([]);
    const [loadingDependencies, setLoadingDependencies] = useState(false);
    
    const [truncateMessage, setTruncateMessage] = useState('');
    const [truncating, setTruncating] = useState(false);

    useEffect(() => {
        if (deleteTableModalConfig && deleteTableAction === 'drop') {
            const fetchDeps = async () => {
                setLoadingDependencies(true);
                try {
                    const res = await databaseApi.getTableDependencies(deleteTableModalConfig.dbName, deleteTableModalConfig.tableName);
                    if (res.data?.Succeeded || res.data?.succeeded) {
                        const deps = res.data.Data || res.data.data || res.data;
                        setTableDependencies(Array.isArray(deps) ? deps : []);
                    }
                } catch (e) {
                    console.error("Failed to fetch dependencies", e);
                }
                setLoadingDependencies(false);
            };
            fetchDeps();
        }
    }, [deleteTableModalConfig, deleteTableAction]);

    const handleDeleteTable = async () => {
        if (!deleteTableModalConfig) return;
        setTruncating(true);
        setTruncateMessage('');
        try {
            const { dbName, tableName: objectName, objectType } = deleteTableModalConfig;
            let query = '';

            if (!objectType || objectType === 'Tables') {
                query = deleteTableAction === 'truncate'
                    ? `USE [${dbName}]; TRUNCATE TABLE [${objectName}];`
                    : `USE [${dbName}]; DROP TABLE [${objectName}];`;
            } else if (objectType === 'StoredProcedures') {
                query = `USE [${dbName}]; DROP PROCEDURE [${objectName}];`;
            } else if (objectType === 'Views') {
                query = `USE [${dbName}]; DROP VIEW [${objectName}];`;
            } else if (objectType === 'Functions') {
                query = `USE [${dbName}]; DROP FUNCTION [${objectName}];`;
            }

            const res = await databaseApi.executeNonQuery(query);
            if (res.data?.Succeeded || res.data?.succeeded || res.data === true) {
                setDeleteTableModalConfig(null);
                fetchObjects(db1, setObjectsDb1, setLoadingDb1);
                fetchObjects(db2, setObjectsDb2, setLoadingDb2);
            } else {
                setTruncateMessage(res.data?.Message || res.data?.ErrorMessage || `Failed to ${deleteTableAction} table.`);
            }
        } catch (error) {
            setTruncateMessage(error.response?.data?.Message || error.response?.data?.ErrorMessage || error.message || 'Error occurred.');
        }
        setTruncating(false);
    };

    const handleTransferRequest = (itemName, objectType, sourceDb, targetDb) => {
        setTransferModalConfig({ itemName, objectType, sourceDb, targetDb });
        setTransferModeSelection(objectType === 'Table' ? 'Data' : 'Schema');
    };

    const executeTransfer = async () => {
        if (!transferModalConfig) return;
        const { itemName, objectType, sourceDb, targetDb } = transferModalConfig;

        setTransferringItem(itemName);
        setTransferringDetails({ itemName, sourceDb, targetDb });
        setTransferMessage({ text: '', type: '' });
        setTransferModalConfig(null);

        try {
            const res = await databaseApi.transferObject(sourceDb, targetDb, itemName, objectType, transferModeSelection, keepIdentity);
            if (res.data?.Succeeded || res.data?.succeeded || res.data === true) {
                setTransferMessage({ text: `${objectType} '${itemName}' transferred successfully!`, type: 'success' });
                setBlinkingItem(itemName);
                setTimeout(() => setBlinkingItem(null), 3000);
                fetchObjects(db1, setObjectsDb1, setLoadingDb1, true);
                fetchObjects(db2, setObjectsDb2, setLoadingDb2, true);
            } else {
                setTransferMessage({ text: `Failed: ${res.data?.Message || res.data?.message || res.data?.ErrorMessage || res.data?.errorMessage}`, type: 'danger' });
            }
        } catch (error) {
            setTransferMessage({ text: `Error: ${error.response?.data?.Message || error.response?.data?.message || error.response?.data?.ErrorMessage || error.response?.data?.errorMessage || error.message}`, type: 'danger' });
        }
        setTransferringItem(null);
        setTransferringDetails(null);
    };

    const handleCompareObjectClick = async (objectName) => {
        if (!db1 || !db2) return;
        setLoadingCompareObject(true);
        setCompareModalConfig({ objectName, objectType: compareSubTab, sourceDef: '', targetDef: '' });
        
        try {
            const res = await databaseApi.compareObject(db1, db2, objectName, compareSubTab);
            
            // Check if response is successful (Succeeded property might be uppercase or lowercase depending on JSON serializer)
            const isSuccess = res.data?.Succeeded !== undefined ? res.data.Succeeded : res.data?.succeeded;
            
            if (isSuccess || res.status === 200) {
                const data = res.data?.Data || res.data?.data || res.data;
                
                setCompareModalConfig({ 
                    objectName, 
                    objectType: compareSubTab, 
                    sourceDef: data?.SourceDefinition || data?.sourceDefinition || '', 
                    targetDef: data?.TargetDefinition || data?.targetDefinition || '' 
                });
            }
        } catch (err) {
            console.error("Compare API Error:", err);
        }
        setLoadingCompareObject(false);
    };

    const confirmDelete = (dbName) => {
        setDbToDelete(dbName);
        setDeletePassword('');
        setDeleteMessage('');
        setShowDeleteModal(true);
    };

    const executeDelete = async () => {
        setDeleteMessage('');
        if (!deletePassword) {
            setDeleteMessage('Password is required.');
            return;
        }
        setDeleting(true);
        try {
            const res = await databaseApi.deleteDatabase(dbToDelete, deletePassword);
            if (res.data?.Succeeded || res.data?.succeeded || res.data === true) {
                setShowDeleteModal(false);
                fetchDatabases();
            } else {
                setDeleteMessage(res.data?.Message || res.data?.message || res.data?.ErrorMessage || res.data?.errorMessage || 'Failed to delete database.');
            }
        } catch (error) {
            setDeleteMessage(error.response?.data?.Message || error.response?.data?.message || error.response?.data?.ErrorMessage || error.response?.data?.errorMessage || error.message);
        }
        setDeleting(false);
    };

    const handleTabChange = (tab) => {
        setActiveTab(tab);
        setCloneMessage('');
        setTransferMessage({ text: '', type: '' });
        setBlinkingItem(null);
    };

    const renderSubTabs = () => {
        const tabs = ['Tables', 'Views', 'StoredProcedures', 'Functions'];
        return (
            <ul className="nav nav-pills mb-4 justify-content-center" style={{ gap: '10px' }}>
                {tabs.map(tab => (
                    <li className="nav-item" key={tab}>
                        <button
                            type="button"
                            className={`nav-link px-4 py-2 ${compareSubTab === tab ? 'active shadow-sm' : 'bg-light text-dark border'}`}
                            onClick={() => setCompareSubTab(tab)}
                            style={{ borderRadius: '20px', transition: 'all 0.2s', fontSize: '0.875rem' }}
                        >
                            {tab.replace(/([A-Z])/g, ' $1').trim()}
                        </button>
                    </li>
                ))}
            </ul>
        );
    };

    const renderUnifiedCompareList = () => {
        const list1 = objectsDb1?.[compareSubTab] || [];
        const list2 = objectsDb2?.[compareSubTab] || [];
        const loading = loadingDb1 || loadingDb2;

        if (loading) return <div className="text-center py-4 w-100"><div className="spinner-border text-primary" /></div>;
        
        const allNames = Array.from(new Set([...list1.map(x => x.Name), ...list2.map(x => x.Name)])).sort((a, b) => a.localeCompare(b));

        if (allNames.length === 0) return (
            <div className="text-center text-muted mt-5 w-100">
                <FontAwesomeIcon icon={faBoxOpen} className="mb-2 text-black-50" style={{ fontSize: '1.5rem' }} />
                <div style={{ fontSize: '0.85rem' }}>No objects found</div>
            </div>
        );

        return (
            <div className="db-table-list scrollbar w-100 px-2 mt-3" style={{ maxHeight: '500px', overflowX: 'hidden' }}>
                {allNames.map((name, idx) => {
                    const obj1 = list1.find(x => x.Name === name);
                    const obj2 = list2.find(x => x.Name === name);
                    const isBlinking = blinkingItem === name;

                    return (
                        <div key={idx} className="d-flex align-items-center mb-2">
                            {/* DB1 SIDE */}
                            <div className="flex-grow-1" style={{ width: 'calc(50% - 40px)', minWidth: 0 }}>
                                {obj1 ? (
                                    <div className={`db-table-item d-flex justify-content-between align-items-center p-2 border rounded ${isBlinking ? 'blink-animation' : ''}`} style={{ backgroundColor: !!obj2 ? '#fff' : '#fff8e1', minHeight: '45px' }}>
                                        <div className="text-truncate pe-2">
                                            <span className="db-table-item-name fw-bold" style={{ fontSize: '0.85rem' }} title={obj1.Name}>{obj1.Name}</span>
                                            {compareSubTab === 'Tables' && <span className="badge bg-secondary rounded-pill ms-2">{obj1.RowCount} rows</span>}
                                            {(obj1.HasError || obj1.hasError) && <span className="badge bg-danger ms-2" style={{ fontSize: '0.65rem' }} title="Has Invalid References"><FontAwesomeIcon icon={faExclamationTriangle} className="me-1" />Error</span>}
                                            {!obj2 && db2 && <span className="badge bg-warning text-dark ms-2" style={{ fontSize: '0.65rem' }}>Missing in {db2}</span>}
                                        </div>
                                        {db1 && db2 && (
                                            <div className="d-flex align-items-center flex-shrink-0">
                                                <button
                                                    type="button"
                                                    className="btn btn-sm btn-outline-danger me-2 d-inline-flex align-items-center justify-content-center"
                                                    onClick={() => {
                                                        setDeleteTableModalConfig({ tableName: obj1.Name, dbName: db1, objectType: compareSubTab });
                                                        setDeleteTableAction(compareSubTab === 'Tables' ? 'truncate' : 'drop');
                                                        setTableDependencies([]);
                                                        setTruncateMessage('');
                                                    }}
                                                    disabled={transferringItem === obj1.Name}
                                                    title={compareSubTab === 'Tables' ? `Truncate data in ${db1}` : `Drop ${compareSubTab.slice(0, -1).toLowerCase()} in ${db1}`}
                                                    style={{ width: '28px', height: '28px', padding: 0, borderRadius: '4px' }}
                                                >
                                                    <FontAwesomeIcon icon={faTrash} style={{ fontSize: '12px' }} />
                                                </button>
                                                <button
                                                    type="button"
                                                    className="btn btn-sm btn-outline-secondary db-transfer-btn"
                                                    onClick={() => handleTransferRequest(obj1.Name, compareSubTab === 'StoredProcedures' ? 'StoredProcedure' : compareSubTab === 'Functions' ? 'Function' : compareSubTab === 'Views' ? 'View' : 'Table', db1, db2)}
                                                    disabled={transferringItem === obj1.Name}
                                                    title={`Transfer from ${db1} to ${db2}`}
                                                    style={{ padding: '4px 10px', fontSize: '0.75rem', borderRadius: '4px', whiteSpace: 'nowrap' }}
                                                >
                                                    {transferringItem === obj1.Name ? <span className="spinner-border spinner-border-sm" /> : 'Transfer ➔'}
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                ) : (
                                    <div className="p-2 border rounded text-muted d-flex align-items-center justify-content-center" style={{ backgroundColor: '#f8f9fa', minHeight: '45px', border: '1px dashed #ced4da !important', opacity: 0.6 }}>
                                        <span style={{ fontSize: '0.8rem', fontStyle: 'italic' }}>Not found</span>
                                    </div>
                                )}
                            </div>

                            {/* CENTER ANIMATION */}
                            <div className="d-flex justify-content-center mx-2 flex-shrink-0" style={{ width: '60px' }}>
                                {transferringDetails?.itemName === name ? (
                                    <div className="w-100">
                                        <div className="transfer-arrow-container" title={`${transferringDetails.sourceDb} ➔ ${transferringDetails.targetDb}`} style={{ height: '16px' }}>
                                            <div className="transfer-arrow-track"></div>
                                            <div className={`transfer-arrow ${transferringDetails.sourceDb === db1 ? 'forward' : 'backward'}`} style={{ fontSize: '1rem' }}>
                                                <FontAwesomeIcon icon={transferringDetails.sourceDb === db1 ? faArrowRight : faArrowLeft} />
                                            </div>
                                        </div>
                                    </div>
                                ) : (
                                    <button
                                        type="button"
                                        className="btn btn-sm btn-light border shadow-sm"
                                        onClick={() => handleCompareObjectClick(obj1?.Name || obj2?.Name)}
                                        title={`Compare ${obj1?.Name || obj2?.Name} definitions`}
                                        style={{ width: '36px', height: '36px', borderRadius: '50%', padding: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                                    >
                                        <FontAwesomeIcon icon={faExchangeAlt} className="text-primary" style={{ fontSize: '1rem' }} />
                                    </button>
                                )}
                            </div>

                            {/* DB2 SIDE */}
                            <div className="flex-grow-1" style={{ width: 'calc(50% - 40px)', minWidth: 0 }}>
                                {obj2 ? (
                                    <div className={`db-table-item d-flex justify-content-between align-items-center p-2 border rounded ${isBlinking ? 'blink-animation' : ''}`} style={{ backgroundColor: !!obj1 ? '#fff' : '#fff8e1', minHeight: '45px' }}>
                                        <div className="text-truncate pe-2">
                                            <span className="db-table-item-name fw-bold" style={{ fontSize: '0.85rem' }} title={obj2.Name}>{obj2.Name}</span>
                                            {compareSubTab === 'Tables' && <span className="badge bg-secondary rounded-pill ms-2">{obj2.RowCount} rows</span>}
                                            {(obj2.HasError || obj2.hasError) && <span className="badge bg-danger ms-2" style={{ fontSize: '0.65rem' }} title="Has Invalid References"><FontAwesomeIcon icon={faExclamationTriangle} className="me-1" />Error</span>}
                                            {!obj1 && db1 && <span className="badge bg-warning text-dark ms-2" style={{ fontSize: '0.65rem' }}>Missing in {db1}</span>}
                                        </div>
                                        {db1 && db2 && (
                                            <div className="d-flex align-items-center flex-shrink-0">
                                                <button
                                                    type="button"
                                                    className="btn btn-sm btn-outline-danger me-2 d-inline-flex align-items-center justify-content-center"
                                                    onClick={() => {
                                                        setDeleteTableModalConfig({ tableName: obj2.Name, dbName: db2, objectType: compareSubTab });
                                                        setDeleteTableAction(compareSubTab === 'Tables' ? 'truncate' : 'drop');
                                                        setTableDependencies([]);
                                                        setTruncateMessage('');
                                                    }}
                                                    disabled={transferringItem === obj2.Name}
                                                    title={compareSubTab === 'Tables' ? `Truncate data in ${db2}` : `Drop ${compareSubTab.slice(0, -1).toLowerCase()} in ${db2}`}
                                                    style={{ width: '28px', height: '28px', padding: 0, borderRadius: '4px' }}
                                                >
                                                    <FontAwesomeIcon icon={faTrash} style={{ fontSize: '12px' }} />
                                                </button>
                                                <button
                                                    type="button"
                                                    className="btn btn-sm btn-outline-secondary db-transfer-btn"
                                                    onClick={() => handleTransferRequest(obj2.Name, compareSubTab === 'StoredProcedures' ? 'StoredProcedure' : compareSubTab === 'Functions' ? 'Function' : compareSubTab === 'Views' ? 'View' : 'Table', db2, db1)}
                                                    disabled={transferringItem === obj2.Name}
                                                    title={`Transfer from ${db2} to ${db1}`}
                                                    style={{ padding: '4px 10px', fontSize: '0.75rem', borderRadius: '4px', whiteSpace: 'nowrap' }}
                                                >
                                                    {transferringItem === obj2.Name ? <span className="spinner-border spinner-border-sm" /> : '⟵ Transfer'}
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                ) : (
                                    <div className="p-2 border rounded text-muted d-flex align-items-center justify-content-center" style={{ backgroundColor: '#f8f9fa', minHeight: '45px', border: '1px dashed #ced4da !important', opacity: 0.6 }}>
                                        <span style={{ fontSize: '0.8rem', fontStyle: 'italic' }}>Not found</span>
                                    </div>
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>
        );
    };

    const actualTables = tableSizes.filter(t => !((t.TableName || t.tableName) || '').startsWith('['));
    const specialRecords = tableSizes.filter(t => ((t.TableName || t.tableName) || '').startsWith('['));

    const customSelectStyles = {
        control: (base, state) => ({
            ...base,
            fontSize: '0.875rem',
            borderColor: state.isFocused ? '#2f5aa2' : base.borderColor,
            boxShadow: state.isFocused ? '0 0 0 1px #2f5aa2' : base.boxShadow,
            '&:hover': {
                borderColor: state.isFocused ? '#2f5aa2' : base.borderColor
            }
        }),
        option: (base, state) => ({
            ...base,
            fontSize: '0.875rem',
            backgroundColor: state.isSelected
                ? '#2f5aa2'
                : state.isFocused
                    ? '#e6eaf3'
                    : 'white',
            color: state.isSelected ? 'white' : '#16191f',
            '&:active': {
                backgroundColor: '#2f5aa2',
                color: 'white'
            }
        }),
        menuPortal: base => ({ ...base, zIndex: 9999 })
    };

    return (
        <>
            <CenteredModal
                show={show}
                title="Database Management"
                onClose={onClose}
                closeText="Close"
                modalType="modal-xl"
                isViewMode={true}
                blurred={false}
            >
                <div className="px-3 pb-0 db-modal-content">
                    {activeTab === 'hub' ? (
                        <>
                            <div className="mb-4 mt-2 px-3">
                                <h5 className="fw-bolder text-body-emphasis mb-2" style={{ letterSpacing: '-0.5px' }}>
                                    Database Administration
                                </h5>
                                <p className="text-muted fw-bold fs-8-1 mb-0">
                                    Welcome to the Database Management control panel. Select a module below to view databases, clone schemas, compare objects, or execute SQL scripts.
                                </p>
                            </div>
                            <div className="row row-cols-1 row-cols-md-2 row-cols-xl-3 g-4 px-3 mb-4">
                            {[
                                { id: 'list', title: 'Databases', icon: faDatabase, desc: 'View and manage all available databases' },
                                { id: 'clone', title: 'Clone Schema', icon: faCopy, desc: 'Clone schemas between databases' },
                                { id: 'compare', title: 'Compare & Transfer', icon: faExchangeAlt, desc: 'Compare schemas and transfer objects' },
                                { id: 'history', title: 'History', icon: faHistory, desc: 'View database operation history' },
                                { id: 'viewData', title: 'View Data', icon: faTable, desc: 'Browse and query database tables' },
                                { id: 'viewScript', title: 'View Script', icon: faCode, desc: 'Generate SQL scripts for objects' }
                            ].map((card) => (
                                <div className="col" key={card.id}>
                                    <div 
                                        className="card h-100 shadow-sm cursor-pointer transition-all animated-border-card"
                                        onClick={() => handleTabChange(card.id)}
                                        style={{ transition: 'all 0.2s ease', borderRadius: '12px', border: 'none', padding: '2px', backgroundColor: 'transparent' }}
                                        onMouseEnter={(e) => {
                                            e.currentTarget.classList.add('shadow');
                                            e.currentTarget.style.transform = 'translateY(-3px)';
                                        }}
                                        onMouseLeave={(e) => {
                                            e.currentTarget.classList.remove('shadow');
                                            e.currentTarget.style.transform = 'none';
                                        }}
                                    >
                                        <div className="card-body p-4 d-flex flex-column animated-border-card-inner" style={{ backgroundColor: '#fff', borderRadius: '10px' }}>
                                            <div className="d-flex justify-content-between align-items-start mb-3">
                                                <div className="p-3 bg-primary-subtle text-primary rounded-3 d-inline-flex align-items-center justify-content-center" style={{ width: '56px', height: '56px' }}>
                                                    <FontAwesomeIcon icon={card.icon} size="lg" />
                                                </div>
                                            </div>
                                            <h5 className="card-title fw-bolder text-body-emphasis mt-2 mb-2" style={{ fontSize: '1.1rem' }}>{card.title}</h5>
                                            <p className="card-text text-muted fw-bold fs-8-1 mb-0 flex-grow-1" style={{ lineHeight: '1.6' }}>
                                                {card.desc}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            ))}
                            </div>
                        </>
                    ) : (
                        <div className="mb-3 border-bottom d-flex align-items-center justify-content-between px-2 pb-2">
                            <h5 className="fw-bolder text-body-emphasis mb-0 fs-5">
                                {
                                    activeTab === 'list' ? 'Databases' :
                                    activeTab === 'clone' ? 'Clone Schema' :
                                    activeTab === 'compare' ? 'Compare & Transfer' :
                                    activeTab === 'history' ? 'History' :
                                    activeTab === 'viewData' ? 'View Data' :
                                    activeTab === 'viewScript' ? 'View Script' : ''
                                }
                            </h5>
                            <button
                                className="btn btn-light border shadow-sm fw-bold d-flex align-items-center px-3 py-1"
                                onClick={() => handleTabChange('hub')}
                            >
                                <FontAwesomeIcon icon={faArrowLeft} className="me-2" /> Back to Hub
                            </button>
                        </div>
                    )}

                    <div className="tab-content">
                        {/* LIST TAB */}
                        {activeTab === 'list' && (
                            <>
                                {selectedDbDetails ? (
                                    <>
                                        <div className="mb-3 d-flex justify-content-between align-items-center">
                                            <div className="d-flex align-items-center">
                                                <button
                                                    className="btn btn-sm btn-link text-decoration-none p-0 me-3"
                                                    onClick={handleBackToList}
                                                >
                                                    <FontAwesomeIcon icon={faArrowLeft} className="me-1" /> Back
                                                </button>
                                                <p className="mb-0 text-muted fs-9 fw-bold">
                                                    Table sizes for database: <span className="text-primary">{selectedDbDetails}</span>
                                                </p>
                                            </div>
                                            <button
                                                className="btn btn-sm btn-outline-primary d-flex align-items-center text-nowrap"
                                                onClick={() => handleViewDetails(selectedDbDetails)}
                                                disabled={loadingDetails}
                                            >
                                                {loadingDetails ? (
                                                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                                ) : (
                                                    <FontAwesomeIcon icon={faSync} className="me-2" />
                                                )}
                                                Refresh
                                            </button>
                                        </div>

                                        {specialRecords.length > 0 && (
                                            <div className="row mb-3 gx-2">
                                                {specialRecords.map((t, idx) => (
                                                    <div className="col-md-4 mb-2 mb-md-0" key={idx}>
                                                        <div className="card bg-light border-0 shadow-sm h-100">
                                                            <div className="card-body p-2 d-flex flex-column justify-content-center text-center">
                                                                <div className="text-muted fw-bold mb-1" style={{ fontSize: '0.7rem' }}>
                                                                    <FontAwesomeIcon icon={faInfoCircle} className="me-1" />
                                                                    {(t.TableName || t.tableName).replace(/\[|\]/g, '')}
                                                                </div>
                                                                <div className="fs-6 fw-bold text-danger">{t.TotalSpaceMB !== undefined ? t.TotalSpaceMB : t.totalSpaceMB} MB</div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        )}

                                        <div className="table-responsive border rounded" style={{ maxHeight: '450px', overflowY: 'auto' }}>
                                            <table className="table table-hover mb-0 fs-9">
                                                <thead className="table-light sticky-top">
                                                    <tr>
                                                        <th width="10%" className="ps-3 py-3">No.</th>
                                                        <th width="50%" className="py-3">Table Name</th>
                                                        <th width="20%" className="text-end py-3">Row Count</th>
                                                        <th width="20%" className="text-end pe-4 py-3">Size (MB)</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {loadingDetails ? (
                                                        <tr>
                                                            <td colSpan="4" className="text-center py-4">
                                                                <div className="spinner-border text-primary" role="status">
                                                                    <span className="visually-hidden">Loading...</span>
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    ) : actualTables.length === 0 ? (
                                                        <tr>
                                                            <td colSpan="4" className="text-center text-muted py-4">
                                                                No tables found in this database!
                                                            </td>
                                                        </tr>
                                                    ) : (
                                                        actualTables.map((t, idx) => (
                                                            <tr key={idx}>
                                                                <td className="ps-3 py-3">{idx + 1}</td>
                                                                <td className="fw-bold text-dark py-3">{t.TableName || t.tableName}</td>
                                                                <td className="text-end fw-medium py-3">{(t.RowCount ?? t.rowCount) !== null ? (t.RowCount ?? t.rowCount) : '-'}</td>
                                                                <td className="text-end fw-medium text-danger pe-4 py-3">{t.TotalSpaceMB !== undefined ? t.TotalSpaceMB : t.totalSpaceMB}</td>
                                                            </tr>
                                                        ))
                                                    )}
                                                </tbody>
                                            </table>
                                        </div>
                                    </>
                                ) : (
                                    <>
                                        <div className="mb-3 d-flex justify-content-between align-items-center">
                                            <p className="mb-0 text-muted fs-9 me-3 fw-bold">
                                                List of all available databases on the server. You can manage them here safely and efficiently.
                                            </p>
                                            <button
                                                className="btn btn-sm btn-outline-primary d-flex align-items-center text-nowrap"
                                                onClick={fetchDatabases}
                                                disabled={loading}
                                            >
                                                {loading ? (
                                                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                                ) : (
                                                    <FontAwesomeIcon icon={faSync} className="me-2" />
                                                )}
                                                Refresh
                                            </button>
                                        </div>

                                        <div className="table-responsive border rounded" style={{ maxHeight: '450px', overflowY: 'auto' }}>
                                            <table className="table table-hover mb-0 fs-9">
                                                <thead className="table-light sticky-top">
                                                    <tr>
                                                        <th width="5%" className="ps-3">No.</th>
                                                        <th width="20%">Database Name</th>
                                                        <th width="15%">Creation Date</th>
                                                        <th width="10%" className="text-end">Size (MB)</th>
                                                        <th width="10%" className="text-end">Tables</th>
                                                        <th width="10%" className="text-end">Procedures</th>
                                                        <th width="10%" className="text-end">Views</th>
                                                        <th width="10%" className="text-end">Functions</th>
                                                        <th width="10%" className="text-center">Actions</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {loading ? (
                                                        <tr>
                                                            <td colSpan="9" className="text-center py-4">
                                                                <div className="spinner-border text-primary" role="status">
                                                                    <span className="visually-hidden">Loading...</span>
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    ) : databases.length === 0 ? (
                                                        <tr>
                                                            <td colSpan="9" className="text-center text-muted py-4">
                                                                No databases found!
                                                            </td>
                                                        </tr>
                                                    ) : (
                                                        databases.map((db, idx) => {
                                                            const dbName = typeof db === 'object' ? db.DatabaseName : db;
                                                            const creationDate = typeof db === 'object' && db.CreationDate ? new Date(db.CreationDate).toLocaleString() : 'N/A';
                                                            const size = typeof db === 'object' && db.SizeMB !== undefined ? db.SizeMB : 'N/A';
                                                            const tables = typeof db === 'object' && db.TableCount !== undefined ? db.TableCount : 'N/A';
                                                            const sps = typeof db === 'object' && db.StoredProcedureCount !== undefined ? db.StoredProcedureCount : 'N/A';
                                                            const views = typeof db === 'object' && db.ViewCount !== undefined ? db.ViewCount : 'N/A';
                                                            const funcs = typeof db === 'object' && db.FunctionCount !== undefined ? db.FunctionCount : 'N/A';

                                                            return (
                                                                <tr key={idx}>
                                                                    <td className="ps-3">{idx + 1}</td>
                                                                    <td className="fw-bold text-primary">
                                                                        <FontAwesomeIcon icon={faDatabase} className="me-2 text-secondary" />
                                                                        {dbName}
                                                                    </td>
                                                                    <td>{creationDate}</td>
                                                                    <td className="text-end fw-medium">{size}</td>
                                                                    <td className="text-end fw-medium">{tables}</td>
                                                                    <td className="text-end fw-medium">{sps}</td>
                                                                    <td className="text-end fw-medium">{views}</td>
                                                                    <td className="text-end fw-medium">{funcs}</td>
                                                                    <td className="text-center">
                                                                        <Tooltip text="View Details" placement="top">
                                                                            <button
                                                                                className="btn btn-outline-info d-inline-flex align-items-center justify-content-center me-2"
                                                                                onClick={() => handleViewDetails(dbName)}
                                                                                style={{ width: '28px', height: '28px', padding: 0 }}
                                                                            >
                                                                                <FontAwesomeIcon icon={faInfoCircle} style={{ fontSize: '12px' }} />
                                                                            </button>
                                                                        </Tooltip>
                                                                        <Tooltip text="Delete Database" placement="top">
                                                                            <button
                                                                                className="btn btn-outline-danger d-inline-flex align-items-center justify-content-center"
                                                                                onClick={() => confirmDelete(dbName)}
                                                                                style={{ width: '28px', height: '28px', padding: 0 }}
                                                                            >
                                                                                <FontAwesomeIcon icon={faTrash} style={{ fontSize: '12px' }} />
                                                                            </button>
                                                                        </Tooltip>
                                                                    </td>
                                                                </tr>
                                                            );
                                                        })
                                                    )}
                                                </tbody>
                                            </table>
                                        </div>
                                    </>
                                )}
                            </>
                        )}

                        {/* CLONE TAB */}
                        {activeTab === 'clone' && (
                            <div className="tab-pane fade show active" role="tabpanel">
                                <div style={{ border: '1px solid #d5dbdb', borderRadius: '8px', overflow: 'hidden' }}>
                                    <div style={{ background: '#f2f3f3', borderBottom: '1px solid #d5dbdb', padding: '12px 20px' }}>
                                        <h6 style={{ margin: 0, fontSize: '0.875rem', fontWeight: 700, color: '#16191f' }}>Clone Schema</h6>
                                        <p style={{ margin: 0, fontSize: '0.78rem', color: '#687078', marginTop: '2px' }}>Create an empty copy of an existing database's tables, views, stored procedures, and functions.</p>
                                    </div>
                                    <div style={{ padding: '20px' }}>
                                        <form onSubmit={handleClone}>
                                            <div className="row g-4 mb-4">
                                                <div className="col-md-6">
                                                    <label className="d-block fw-bold mb-0" style={{ fontSize: '0.875rem', color: '#16191f' }}>
                                                        Source Database <span className="text-danger">*</span>
                                                    </label>
                                                    <p style={{ fontSize: '0.78rem', color: '#687078', margin: '2px 0 6px 0' }}>
                                                        Select the database you want to clone from.
                                                    </p>
                                                    <div className="mt-2">
                                                        <Select
                                                            options={databases.map(db => { const dbName = typeof db === 'object' ? db.DatabaseName : db; return { value: dbName, label: dbName }; })}
                                                            value={cloneSource ? { value: cloneSource, label: cloneSource } : null}
                                                            onChange={(selectedOption) => setCloneSource(selectedOption ? selectedOption.value : '')}
                                                            placeholder="Select source database..."
                                                            isClearable
                                                            menuPortalTarget={document.body}
                                                            menuPosition="fixed"
                                                            styles={customSelectStyles}
                                                        />
                                                    </div>
                                                </div>
                                                <div className="col-md-6">
                                                    <label className="d-block fw-bold mb-0" style={{ fontSize: '0.875rem', color: '#16191f' }}>
                                                        Target Database Name <span className="text-danger">*</span>
                                                    </label>
                                                    <p style={{ fontSize: '0.78rem', color: '#687078', margin: '2px 0 6px 0' }}>
                                                        Enter a unique name for the new cloned database.
                                                    </p>
                                                    <input type="text" className="form-control mt-2" value={cloneTarget} onChange={(e) => setCloneTarget(e.target.value)} placeholder="e.g. BTAIChatbotAPI_Clone" required />
                                                </div>
                                            </div>
                                            {cloneMessage && (
                                                cloneMessage.includes('successfully') ? (
                                                    <div className="auth-success-message d-flex align-items-center mb-4 text-start">
                                                        <span className="error-text">{cloneMessage}</span>
                                                    </div>
                                                ) : (
                                                    <div className="auth-error-message d-flex align-items-center mb-4">
                                                        <div className="error-icon me-2 text-danger">
                                                            <FontAwesomeIcon icon={faExclamationTriangle} />
                                                        </div>
                                                        <span className="error-text text-danger">{cloneMessage}</span>
                                                    </div>
                                                )
                                            )}
                                            <div style={{ borderTop: '1px solid #eaeded', paddingTop: '16px' }}>
                                                {showLogsPanel ? (
                                                    <div className="w-100">
                                                        <div className="d-flex justify-content-between mb-1" style={{ fontSize: '0.875rem' }}>
                                                            <span className="fw-bold text-primary">{cloning ? "Cloning schema..." : "Cloning finished."} {cloneTimeLeft && <span className="ms-2 text-muted fw-normal">({cloneTimeLeft})</span>}</span>
                                                            <span className="fw-bold text-primary">{Math.round(cloneProgress)}%</span>
                                                        </div>
                                                        <div className="progress" style={{ height: '8px', borderRadius: '4px' }}>
                                                            <div
                                                                className="progress-bar progress-bar-striped progress-bar-animated bg-primary"
                                                                role="progressbar"
                                                                style={{ width: `${cloneProgress}%`, transition: 'width 0.2s linear' }}
                                                                aria-valuenow={cloneProgress}
                                                                aria-valuemin="0"
                                                                aria-valuemax="100"
                                                            ></div>
                                                        </div>
                                                        {cloneLogs.length > 0 && (
                                                            <div className="mt-3 bg-dark text-light p-2 rounded text-start" style={{ height: '150px', overflowY: 'auto', fontSize: '0.8rem', fontFamily: 'monospace' }}>
                                                                {cloneLogs.map((log, idx) => (
                                                                    <div key={idx} style={{ borderBottom: '1px solid #333', padding: '2px 0' }}>
                                                                        <span style={{ color: '#4caf50' }}>[{new Date().toLocaleTimeString()}]</span> <span style={{ color: log.includes('[WARNING]') || log.includes('[ERROR]') ? '#ff4d4f' : 'inherit' }}>{log}</span>
                                                                    </div>
                                                                ))}
                                                                <div ref={(el) => { el && el.scrollIntoView() }} />
                                                            </div>
                                                        )}
                                                        {!cloning && (
                                                            <div className="d-flex justify-content-end mt-3">
                                                                <button type="button" className="btn btn-secondary" onClick={() => { setShowLogsPanel(false); setCloneLogs([]); setCloneMessage(''); setCloneProgress(0); setCloneTimeLeft(''); }}>Close Logs</button>
                                                            </div>
                                                        )}
                                                    </div>
                                                ) : (
                                                    <div className="d-flex justify-content-end">
                                                        <button type="submit" className="btn btn-primary">
                                                            Clone Schema
                                                        </button>
                                                    </div>
                                                )}
                                            </div>
                                        </form>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* COMPARE TAB */}
                        {activeTab === 'compare' && (
                            <div className="tab-pane fade show active" role="tabpanel">
                                <div style={{ border: '1px solid #d5dbdb', borderRadius: '8px', overflow: 'hidden' }}>
                                    <div style={{ background: '#f2f3f3', borderBottom: '1px solid #d5dbdb', padding: '12px 20px' }}>
                                        <h6 style={{ margin: 0, fontSize: '0.875rem', fontWeight: 700, color: '#16191f' }}>Compare & Transfer Data</h6>
                                        <p style={{ margin: 0, fontSize: '0.78rem', color: '#687078', marginTop: '2px' }}>Copy data from tables in Database 1 to tables in Database 2.</p>
                                    </div>
                                    <div style={{ padding: '20px' }}>
                                        <div className="row mb-4 g-4">
                                            <div className="col-md-6">
                                                <label className="d-block fw-bold mb-0" style={{ fontSize: '0.875rem', color: '#16191f' }}>
                                                    Database 1 (Source)
                                                </label>
                                                <p style={{ fontSize: '0.78rem', color: '#687078', margin: '2px 0 6px 0' }}>
                                                    Select the database to read data from.
                                                </p>
                                                <div className="mt-2">
                                                    <Select
                                                        options={databases.map(db => { const dbName = typeof db === 'object' ? db.DatabaseName : db; return { value: dbName, label: dbName }; })}
                                                        value={db1 ? { value: db1, label: db1 } : null}
                                                        onChange={(selectedOption) => setDb1(selectedOption ? selectedOption.value : '')}
                                                        placeholder="Select database 1..."
                                                        isClearable
                                                        menuPortalTarget={document.body}
                                                        menuPosition="fixed"
                                                        styles={customSelectStyles}
                                                    />
                                                </div>
                                            </div>
                                            <div className="col-md-6">
                                                <label className="d-block fw-bold mb-0" style={{ fontSize: '0.875rem', color: '#16191f' }}>
                                                    Database 2 (Target)
                                                </label>
                                                <p style={{ fontSize: '0.78rem', color: '#687078', margin: '2px 0 6px 0' }}>
                                                    Select the database to transfer data to.
                                                </p>
                                                <div className="mt-2">
                                                    <Select
                                                        options={databases.map(db => { const dbName = typeof db === 'object' ? db.DatabaseName : db; return { value: dbName, label: dbName }; })}
                                                        value={db2 ? { value: db2, label: db2 } : null}
                                                        onChange={(selectedOption) => setDb2(selectedOption ? selectedOption.value : '')}
                                                        placeholder="Select database 2..."
                                                        isClearable
                                                        menuPortalTarget={document.body}
                                                        menuPosition="fixed"
                                                        styles={customSelectStyles}
                                                    />
                                                </div>
                                            </div>
                                        </div>

                                        {transferMessage.text && (
                                            transferMessage.type === 'success' ? (
                                                <div className="alert alert-success py-2 mb-4 fs-9">
                                                    {transferMessage.text}
                                                </div>
                                            ) : (
                                                <div className="auth-error-message d-flex align-items-center mb-4">
                                                    <div className="error-icon me-2 text-danger">
                                                        <FontAwesomeIcon icon={faExclamationTriangle} />
                                                    </div>
                                                    <span className="error-text text-danger">{transferMessage.text}</span>
                                                </div>
                                            )
                                        )}

                                        <div style={{ borderTop: '1px solid #eaeded', paddingTop: '16px' }}>
                                            {renderSubTabs()}
                                            <div className="db-compare-container" style={{ display: 'flex', flexDirection: 'column' }}>
                                                <div className="d-flex w-100 justify-content-between align-items-start mb-2">
                                                    <div className="text-center" style={{ width: 'calc(50% - 40px)' }}>
                                                        <h6 className={db1 && db2 ? "mb-1" : "mb-3"}>{db1 || 'Database 1'}</h6>
                                                        {db1 && db2 && !loadingDb1 && !loadingDb2 && (
                                                            <div className="text-center">
                                                                {(() => {
                                                                    const missing1 = objectsDb1?.[compareSubTab]?.filter(t => !objectsDb2?.[compareSubTab]?.some(x => x.Name === t.Name)).length || 0;
                                                                    return missing1 > 0 
                                                                        ? <span className="badge bg-warning text-dark px-2 py-1">Missing {missing1} in {db2}</span>
                                                                        : <span className="badge bg-success bg-opacity-75 px-2 py-1">All {compareSubTab} matched in {db2}</span>;
                                                                })()}
                                                            </div>
                                                        )}
                                                    </div>

                                                    <div className="text-center" style={{ width: '60px' }}>
                                                        {/* Center space between DB names */}
                                                    </div>

                                                    <div className="text-center" style={{ width: 'calc(50% - 40px)' }}>
                                                        <h6 className={db1 && db2 ? "mb-1" : "mb-3"}>{db2 || 'Database 2'}</h6>
                                                        {db1 && db2 && !loadingDb1 && !loadingDb2 && (
                                                            <div className="text-center">
                                                                {(() => {
                                                                    const missing2 = objectsDb2?.[compareSubTab]?.filter(t => !objectsDb1?.[compareSubTab]?.some(x => x.Name === t.Name)).length || 0;
                                                                    return missing2 > 0 
                                                                        ? <span className="badge bg-warning text-dark px-2 py-1">Missing {missing2} in {db1}</span>
                                                                        : <span className="badge bg-success bg-opacity-75 px-2 py-1">All {compareSubTab} matched in {db1}</span>;
                                                                })()}
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>

                                                {/* Unified List Rendering */}
                                                {renderUnifiedCompareList()}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                        {/* HISTORY TAB */}
                        {activeTab === 'history' && (
                            <HistoryTab />
                        )}

                        {/* VIEW DATA TAB */}
                        {activeTab === 'viewData' && (
                            <div className="p-3">
                                <div className="row mb-4">
                                    <div className="col-md-5">
                                        <label className="fw-bold mb-1" style={{ fontSize: '0.875rem' }}>Select Database</label>
                                        <Select
                                            options={databases.map(db => { const dbName = typeof db === 'object' ? db.DatabaseName : db; return { value: dbName, label: dbName }; })}
                                            value={viewDataSelectedDb ? { value: viewDataSelectedDb, label: viewDataSelectedDb } : null}
                                            onChange={(opt) => setViewDataSelectedDb(opt ? opt.value : null)}
                                            placeholder="Choose a database..."
                                            isClearable
                                            styles={customSelectStyles}
                                            menuPortalTarget={document.body}
                                        />
                                    </div>
                                    <div className="col-md-5">
                                        <label className="fw-bold mb-1" style={{ fontSize: '0.875rem' }}>Select Table</label>
                                        <Select
                                            options={viewDataTables.map(t => ({ value: t, label: `[${t.SchemaName}].[${t.TableName}]` }))}
                                            value={viewDataSelectedTable ? { value: viewDataSelectedTable, label: `[${viewDataSelectedTable.SchemaName}].[${viewDataSelectedTable.TableName}]` } : null}
                                            onChange={(opt) => {
                                                setViewDataSelectedTable(opt ? opt.value : null);
                                                setPageIndex(0); // reset page on table change
                                            }}
                                            placeholder={viewDataLoading && !viewDataTables.length ? "Loading tables..." : "Choose a table..."}
                                            isClearable
                                            isDisabled={!viewDataSelectedDb || (viewDataLoading && !viewDataTables.length)}
                                            styles={customSelectStyles}
                                            menuPortalTarget={document.body}
                                        />
                                    </div>
                                    <div className="col-md-2 d-flex align-items-end">
                                        <button 
                                            className="btn btn-primary w-100"
                                            onClick={() => { setPageIndex(0); handleFetchViewData(0, pageSize); }}
                                            disabled={!viewDataSelectedDb || !viewDataSelectedTable || viewDataLoading}
                                        >
                                            {viewDataLoading ? <span className="spinner-border spinner-border-sm" /> : <><FontAwesomeIcon icon={faSync} className="me-2" />Fetch</>}
                                        </button>
                                    </div>
                                </div>

                                {viewDataError && (
                                    <div className="alert alert-danger p-2 fs-9 mb-3">
                                        <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
                                        {viewDataError}
                                    </div>
                                )}

                                {viewDataColumns.length > 0 && (
                                    <div className="border rounded d-flex flex-column" style={{ maxHeight: '500px' }}>
                                        <div className="table-responsive flex-grow-1" style={{ overflow: 'auto', minHeight: 0 }}>
                                            <table className="table table-hover table-bordered mb-0" style={{ fontSize: '0.85rem' }}>
                                                <thead className="table-light" style={{ position: 'sticky', top: 0, zIndex: 1 }}>
                                                    <tr>
                                                        {viewDataColumns.map((col, idx) => (
                                                            <th key={idx} className={`text-nowrap ${idx === 0 ? 'ps-3' : ''}`}>{col}</th>
                                                        ))}
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {viewDataRows.length === 0 ? (
                                                        <tr>
                                                            <td colSpan={viewDataColumns.length} className="text-center py-4 text-muted">
                                                                No data available in this table.
                                                            </td>
                                                        </tr>
                                                    ) : (
                                                        viewDataRows.map((row, rIdx) => (
                                                            <tr key={rIdx}>
                                                                {viewDataColumns.map((col, cIdx) => (
                                                                    <td key={cIdx} className={`text-nowrap ${cIdx === 0 ? 'ps-3' : ''}`} style={{ maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis' }} title={String(row[col] ?? 'NULL')}>
                                                                        {row[col] === null ? <span className="text-muted fst-italic">NULL</span> : String(row[col])}
                                                                    </td>
                                                                ))}
                                                            </tr>
                                                        ))
                                                    )}
                                                </tbody>
                                            </table>
                                        </div>
                                        
                                        {/* SSMS Style Pagination Footer */}
                                        <div className="bg-light border-top p-2 d-flex align-items-center justify-content-between" style={{ fontSize: '0.85rem' }}>
                                            <div className="d-flex align-items-center">
                                                <button 
                                                    className="btn btn-sm btn-light border me-1 px-2"
                                                    disabled={pageIndex === 0 || viewDataLoading}
                                                    onClick={() => setPageIndex(0)}
                                                    title="First Page"
                                                >
                                                    <FontAwesomeIcon icon={faAngleDoubleLeft} />
                                                </button>
                                                <button 
                                                    className="btn btn-sm btn-light border me-3 px-2"
                                                    disabled={pageIndex === 0 || viewDataLoading}
                                                    onClick={() => setPageIndex(p => Math.max(0, p - 1))}
                                                    title="Previous Page"
                                                >
                                                    <FontAwesomeIcon icon={faAngleLeft} />
                                                </button>
                                                
                                                <div className="d-flex align-items-center me-3">
                                                    <span className="me-2 text-muted">Rows</span>
                                                    <select 
                                                        className="form-select form-select-sm"
                                                        style={{ minWidth: '90px', fontSize: '0.85rem' }}
                                                        value={pageSize}
                                                        onChange={(e) => {
                                                            setPageSize(Number(e.target.value));
                                                            setPageIndex(0);
                                                        }}
                                                        disabled={viewDataLoading}
                                                    >
                                                        <option value="10">10</option>
                                                        <option value="100">100</option>
                                                        <option value="250">250</option>
                                                        <option value="500">500</option>
                                                        <option value="1000">1000</option>
                                                    </select>
                                                </div>

                                                <span className="text-muted fw-bold">
                                                    {totalRows > 0 ? (
                                                        `${(pageIndex * pageSize) + 1} - ${Math.min((pageIndex + 1) * pageSize, totalRows)} of ${totalRows.toLocaleString()}`
                                                    ) : (
                                                        "0 of 0"
                                                    )}
                                                </span>

                                                <button 
                                                    className="btn btn-sm btn-light border ms-3 px-2"
                                                    disabled={(pageIndex + 1) * pageSize >= totalRows || viewDataLoading}
                                                    onClick={() => setPageIndex(p => p + 1)}
                                                    title="Next Page"
                                                >
                                                    <FontAwesomeIcon icon={faAngleRight} />
                                                </button>
                                                <button 
                                                    className="btn btn-sm btn-light border ms-1 px-2"
                                                    disabled={(pageIndex + 1) * pageSize >= totalRows || viewDataLoading}
                                                    onClick={() => setPageIndex(Math.max(0, Math.ceil(totalRows / pageSize) - 1))}
                                                    title="Last Page"
                                                >
                                                    <FontAwesomeIcon icon={faAngleDoubleRight} />
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}

                        {/* VIEW SCRIPT TAB */}
                        {activeTab === 'viewScript' && (
                            <ViewScriptTab 
                                databases={databases.map(db => { const dbName = typeof db === 'object' ? db.DatabaseName : db; return { value: dbName, label: dbName }; })}
                                customSelectStyles={customSelectStyles}
                            />
                        )}
                    </div>
                </div>
            </CenteredModal>

            {showDeleteModal && (
                <CenteredModal
                    show={showDeleteModal}
                    onClose={() => setShowDeleteModal(false)}
                    modalType="modal-md"
                    zIndex={1070}
                    onConfirm={executeDelete}
                    confirmText="Permanently Delete"
                    isSubmitting={deleting}
                    submittingText="Deleting..."
                    confirmBtnClass="btn btn-danger"
                    disabledConfirmBtn={!deletePassword || deleting}
                    blurred={false}
                >
                    <div className="p-4 text-center">
                        <div className="d-inline-flex align-items-center justify-content-center bg-danger bg-opacity-10 text-danger rounded-circle mb-3" style={{ width: '80px', height: '80px' }}>
                            <FontAwesomeIcon icon={faTrash} style={{ fontSize: '2.5rem' }} />
                        </div>
                        <h4 className="fw-bold mb-2 text-dark">Delete Database?</h4>
                        <p className="text-muted mb-4 fs-9 px-2">
                            You are about to permanently drop the database <strong className="text-danger">{dbToDelete}</strong>.
                            <br />This action is irreversible and all data will be lost.
                        </p>

                        {deleteMessage && (
                            <div className="auth-error-message mb-3 text-start">
                                <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
                                <span>{deleteMessage}</span>
                            </div>
                        )}

                        <div className="bg-light p-3 rounded border text-start mb-2">
                            <label className="fw-bold mb-2 text-dark" style={{ fontSize: '0.85rem' }}>
                                To confirm, enter Super Admin Password <span className="text-danger">*</span>
                            </label>
                            <div className="input-group">
                                <input
                                    type={showPassword ? "text" : "password"}
                                    className="form-control py-3"
                                    placeholder="Enter password..."
                                    value={deletePassword}
                                    onChange={e => setDeletePassword(e.target.value)}
                                    autoFocus
                                />
                                <button
                                    className="btn btn-light border px-3 py-3"
                                    type="button"
                                    onClick={() => setShowPassword(!showPassword)}
                                    title={showPassword ? "Hide password" : "Show password"}
                                >
                                    <FontAwesomeIcon icon={showPassword ? faEyeSlash : faEye} className="text-muted" />
                                </button>
                            </div>
                        </div>
                    </div>
                </CenteredModal>
            )}

            {transferModalConfig && (
                <CenteredModal
                    show={!!transferModalConfig}
                    onClose={() => setTransferModalConfig(null)}
                    modalType="modal-md"
                    zIndex={1070}
                    onConfirm={executeTransfer}
                    confirmText="Transfer"
                    isSubmitting={transferringItem !== null}
                    submittingText="Transferring..."
                    confirmBtnClass="btn btn-primary"
                >
                    <div className="p-4 text-center">
                        <div className="d-inline-flex align-items-center justify-content-center bg-primary bg-opacity-10 text-primary rounded-circle mb-3" style={{ width: '80px', height: '80px' }}>
                            <FontAwesomeIcon icon={faExchangeAlt} style={{ fontSize: '2.5rem' }} />
                        </div>
                        <h4 className="fw-bold mb-2 text-dark">Transfer {transferModalConfig.objectType}</h4>
                        <p className="text-muted mb-3 px-2">
                            Transferring <strong>{transferModalConfig.itemName}</strong> from <strong>{transferModalConfig.sourceDb}</strong> to <strong>{transferModalConfig.targetDb}</strong>.
                        </p>

                        {transferModalConfig.objectType === 'Table' && (
                            <div className="bg-light p-3 rounded border text-start mb-2">
                                <label className="fw-bold mb-2 text-dark" style={{ fontSize: '0.85rem' }}>
                                    Select Transfer Mode <span className="text-danger">*</span>
                                </label>
                                <select
                                    className="form-select py-3 mb-3"
                                    value={transferModeSelection}
                                    onChange={(e) => setTransferModeSelection(e.target.value)}
                                >
                                    <option value="Data">Data Only (Append)</option>
                                    <option value="Schema">Schema Only (Overwrite Target)</option>
                                    <option value="Both">Both Schema and Data</option>
                                </select>
                                
                                {(transferModeSelection === 'Data' || transferModeSelection === 'Both') && (
                                    <div className="form-check mb-3 ms-1">
                                        <input 
                                            className="form-check-input shadow-none" 
                                            type="checkbox" 
                                            id="keepIdentityCheck" 
                                            checked={keepIdentity}
                                            onChange={(e) => setKeepIdentity(e.target.checked)}
                                        />
                                        <label className="form-check-label fw-bold text-dark" style={{ fontSize: '0.85rem' }} htmlFor="keepIdentityCheck">
                                            Keep Identity (IDENTITY_INSERT)
                                        </label>
                                        <div className="text-muted" style={{ fontSize: '0.75rem', marginTop: '2px' }}>
                                            Retain the original identity values when inserting data into the target table.
                                        </div>
                                    </div>
                                )}

                                {(transferModeSelection === 'Schema' || transferModeSelection === 'Both') && (
                                    <div className="alert alert-danger mb-0" style={{ fontSize: '0.85rem' }}>
                                        <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
                                        <strong>Warning:</strong> Transferring schema will DROP the table if it already exists, wiping out its current data!
                                    </div>
                                )}
                            </div>
                        )}
                        {transferModalConfig.objectType !== 'Table' && (
                            <div className="alert alert-warning text-start mb-0" style={{ fontSize: '0.85rem' }}>
                                <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
                                If it already exists in the target database, it will be automatically overwritten (CREATE OR ALTER).
                            </div>
                        )}
                    </div>
                </CenteredModal>
            )}
            {deleteTableModalConfig && (
                <CenteredModal
                    show={!!deleteTableModalConfig}
                    onClose={() => setDeleteTableModalConfig(null)}
                    zIndex={1070}
                    blurred={false}
                    title={deleteTableAction === 'truncate' ? "Confirm Truncate Table" : `Confirm Drop ${deleteTableModalConfig?.objectType ? deleteTableModalConfig.objectType.slice(0, -1) : 'Table'}`}
                    closeText="Cancel"
                    onConfirm={handleDeleteTable}
                    confirmText={<><FontAwesomeIcon icon={faTrash} className="me-2" />{deleteTableAction === 'truncate' ? 'Truncate Table' : 'Drop'}</>}
                    confirmBtnClass="btn btn-danger"
                    isSubmitting={truncating}
                    submittingText={deleteTableAction === 'truncate' ? "Truncating..." : "Dropping..."}
                >
                    <div className="p-3 text-center">
                        <div className="mb-3 text-danger">
                            <FontAwesomeIcon icon={faExclamationTriangle} style={{ fontSize: '3rem' }} />
                        </div>
                        <h5 className="fw-bold text-dark mb-2">Are you sure?</h5>
                        <p className="text-muted" style={{ fontSize: '0.9rem' }}>
                            You are about to {deleteTableAction} <strong className="text-danger">{deleteTableModalConfig.tableName}</strong> in database <strong className="text-primary">{deleteTableModalConfig.dbName}</strong>.
                        </p>

                        {(!deleteTableModalConfig.objectType || deleteTableModalConfig.objectType === 'Tables') && (
                            <div className="bg-light p-3 rounded border text-start mb-3 mx-auto" style={{ maxWidth: '400px' }}>
                                <label className="fw-bold mb-2 text-dark" style={{ fontSize: '0.85rem' }}>Select Action</label>
                                <div className="form-check mb-2">
                                    <input 
                                        className="form-check-input" 
                                        type="radio" 
                                        name="deleteTableAction" 
                                        id="actionTruncate" 
                                        value="truncate"
                                        checked={deleteTableAction === 'truncate'}
                                        onChange={() => setDeleteTableAction('truncate')}
                                    />
                                    <label className="form-check-label fw-bold text-dark" style={{ fontSize: '0.85rem' }} htmlFor="actionTruncate">
                                        Truncate (Clear Data)
                                    </label>
                                    <div className="text-muted" style={{ fontSize: '0.75rem', marginTop: '2px' }}>
                                        Deletes all rows quickly but leaves the table structure intact.
                                    </div>
                                </div>
                                <div className="form-check">
                                    <input 
                                        className="form-check-input" 
                                        type="radio" 
                                        name="deleteTableAction" 
                                        id="actionDrop" 
                                        value="drop"
                                        checked={deleteTableAction === 'drop'}
                                        onChange={() => setDeleteTableAction('drop')}
                                    />
                                    <label className="form-check-label fw-bold text-danger" style={{ fontSize: '0.85rem' }} htmlFor="actionDrop">
                                        Drop (Delete Table Completely)
                                    </label>
                                    <div className="text-muted" style={{ fontSize: '0.75rem', marginTop: '2px' }}>
                                        Removes the table structure and all data. Cannot be undone.
                                    </div>
                                </div>
                            </div>
                        )}

                        {deleteTableAction === 'drop' && (
                            <div className="bg-danger bg-opacity-10 p-3 rounded border border-danger text-start mb-4 mx-auto" style={{ maxWidth: '400px', maxHeight: '180px', overflowY: 'auto' }}>
                                <label className="fw-bold text-danger mb-2" style={{ fontSize: '0.85rem' }}>
                                    <FontAwesomeIcon icon={faExclamationCircle} className="me-2" />
                                    Dependent Objects Check
                                </label>
                                {loadingDependencies ? (
                                    <div className="text-muted" style={{ fontSize: '0.8rem' }}>
                                        <span className="spinner-border spinner-border-sm me-2"></span> Checking dependencies...
                                    </div>
                                ) : tableDependencies.length > 0 ? (
                                    <>
                                        <div className="text-danger mb-2 fw-bold" style={{ fontSize: '0.8rem' }}>
                                            The following {tableDependencies.length} objects depend on this table:
                                        </div>
                                        <ul className="mb-0 text-dark" style={{ fontSize: '0.8rem', paddingLeft: '20px' }}>
                                            {tableDependencies.map((dep, idx) => (
                                                <li key={idx}><strong>{dep.ObjectName || dep.objectName}</strong> <span className="badge bg-secondary ms-1">{dep.ObjectType || dep.objectType}</span></li>
                                            ))}
                                        </ul>
                                    </>
                                ) : (
                                    <div className="text-success fw-bold" style={{ fontSize: '0.8rem' }}>
                                        <FontAwesomeIcon icon={faCheckCircle} className="me-2" />
                                        No dependent objects found. Safe to drop.
                                    </div>
                                )}
                            </div>
                        )}

                        {truncateMessage && (
                            <div className="auth-error-message d-flex align-items-center mb-4 mx-auto" style={{ maxWidth: '400px' }}>
                                <div className="error-icon me-2 text-danger">
                                    <FontAwesomeIcon icon={faExclamationTriangle} />
                                </div>
                                <span className="error-text text-danger text-start">{truncateMessage}</span>
                            </div>
                        )}
                    </div>
                </CenteredModal>
            )}

            {compareModalConfig && (
                <CenteredModal
                    show={!!compareModalConfig}
                    title={`Compare ${compareModalConfig.objectType.replace(/s$/, '')}: ${compareModalConfig.objectName}`}
                    onClose={() => setCompareModalConfig(null)}
                    zIndex={1070}
                    blurred={false}
                    closeText="Close"
                    modalType="modal-xl"
                    isViewMode={true}
                >
                    <div className="p-0" style={{ maxHeight: '75vh', overflowY: 'auto', backgroundColor: '#fff' }}>
                        {loadingCompareObject ? (
                            <div className="text-center py-5">
                                <span className="spinner-border spinner-border-lg text-primary mb-3"></span>
                                <div className="text-muted fw-bold">Fetching definitions from both databases...</div>
                            </div>
                        ) : (
                            <ReactDiffViewer
                                oldValue={compareModalConfig.sourceDef}
                                newValue={compareModalConfig.targetDef}
                                splitView={true}
                                leftTitle={db1}
                                rightTitle={db2}
                                hideLineNumbers={false}
                                useDarkTheme={false}
                                styles={{
                                    variables: {
                                        dark: {
                                            diffViewerBackground: '#2e303c',
                                            addedBackground: '#044B53',
                                            addedColor: 'white',
                                            removedBackground: '#632F34',
                                            removedColor: 'white',
                                            wordAddedBackground: '#055d67',
                                            wordRemovedBackground: '#7d383f',
                                            addedGutterBackground: '#034148',
                                            removedGutterBackground: '#632b30',
                                            gutterBackground: '#2c2c2c',
                                            gutterBackgroundDark: '#262626',
                                            highlightBackground: '#2a3967',
                                            highlightGutterBackground: '#2d4077',
                                            codeFoldGutterBackground: '#21232b',
                                            codeFoldBackground: '#262831',
                                            emptyLineBackground: '#363946',
                                            gutterColor: '#464c67',
                                            addedGutterColor: '#8c8c8c',
                                            removedGutterColor: '#8c8c8c',
                                            codeFoldContentColor: '#555a7b',
                                        }
                                    },
                                    line: {
                                        wordBreak: 'break-all'
                                    }
                                }}
                            />
                        )}
                    </div>
                </CenteredModal>
            )}
        </>
    );
}

export default DatabaseManagementModal;
